import React, { useState, useEffect, useCallback, useRef } from "react";

import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { Container, Image, Form, Spinner } from "react-bootstrap";

import "bootstrap/dist/css/bootstrap.min.css";
import "./signup-verifyphone.css";

import logo2 from "../../../assets/images/logo2.png";
import responsivebg from "../../../assets/images/responsive-bg.png";
import backarrow from "../../../assets/images/backarrow.jpg";

import Stepper from "../Stepper";
import { useCookies } from "../../../hooks/useCookies";
import { useAppContext } from "../../../Context/UseContext";
import * as verificationAction from "../../../actions/verification";
import { useGeolocation, DEFAULT_COUNTRY } from "../../../utils/geolocationUtils.jsx";
import { useRateLimit } from "../../../hooks/useRateLimit";

export const SignupPhone = () => {
  const recaptchaRef = useRef(null);

  const navigate = useNavigate();
  const { setCookie } = useCookies();
  const { detectCountry } = useGeolocation();
  const { phoneNumber, setPhoneNumber } = useAppContext();

  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);

  // Rate limiting hook
  const {
    isRateLimited,
    timeRemaining,
    handleRateLimitError,
    formatTimeRemaining,
    getRateLimitMessage
  } = useRateLimit('signup');


  // Geolocation effect
  useEffect(() => {
    let isMounted = true;

    const initializeCountry = async () => {
      try {
        const detectedCountry = await detectCountry();
        if (detectedCountry && isMounted) {
          setSelectedCountry(detectedCountry);
        }
      } catch (error) {
        console.warn("Failed to initialize country detection:", error);
      }
    };

    initializeCountry();

    return () => {
      isMounted = false;
    };
  }, [detectCountry]);

  // Clear specific cookies on component mount (preserve phoneNumber if it exists)
  useEffect(() => {
    // Only clear cookies that are not needed for the signup flow
    // Preserve phoneNumber cookie in case user navigates back
    const cookiesToPreserve = ['phoneNumber'];
    const allCookies = document.cookie.split(';');

    allCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      if (!cookiesToPreserve.includes(cookieName)) {
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
      }
    });
  }, []);

  const handlePhoneNumberChange = useCallback((value) => {
    setPhoneNumber(value || "");
    setErrorMessage(""); // Clear error when user starts typing
  }, [setPhoneNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSendingOtp || isRateLimited) return;

    // Validate phone number
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      setErrorMessage("*Please enter a valid phone number.");
      return;
    }

    setIsSendingOtp(true);
    setErrorMessage("");

    try {

      recaptchaRef.current.reset();
      const token = await recaptchaRef.current.executeAsync();

      const response = await verificationAction.signupOtp({
        phone: phoneNumber,
        recaptchaToken: token
      });

      if (response && response.status === true) {
        setCookie("phoneNumber", phoneNumber, 7);
        navigate("/entercode");
      } else {
        // Check for rate limit error
        if (handleRateLimitError(response)) {
          setErrorMessage(getRateLimitMessage());
        } else {
          setErrorMessage(response?.response?.data.message || response?.message || "*Failed to send code.");
        }
      }
    } catch (error) {
      console.error("OTP error:", error);
      setErrorMessage("*An error occurred. Please try again later.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleBackNavigation = useCallback(() => {
    window.history.back();
  }, []);

  return (
    <div className="signupphone-container" role="main">
      <div className="signup-phone-bg">
        <Image
          className="responsive-bg"
          src={responsivebg}
          alt="Background"
          loading="lazy"
        />
      </div>
      <Container className="signupphone-main">
        <Container className="signupphone-content">
          <Container className="logo-progressbar1">
            <Container className="logo-arrow1">
              <Image
                src={backarrow}
                className="backarrow"
                onClick={handleBackNavigation}
                alt="Go back"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleBackNavigation();
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
              <Image
                src={logo2}
                alt="Logo"
                className="logo1"
                style={{ backgroundColor: "transparent" }}
                loading="lazy"
              />
            </Container>
            <Stepper activeStep="Phone" />
          </Container>

          <Container className="verify-phone-details">
            <Form onSubmit={handleSubmit} className="verify-phone-form">
              <Form.Group
                className="verify-form-group"
              >
                <Form.Label className="num-verify-lable" htmlFor="phoneInput">
                  What's your phone number?
                </Form.Label>
                <div className="d-flex align-items-center w-100">
                  <PhoneInput
                    international
                    defaultCountry={selectedCountry}
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    className={`num-verify-input-container ${errorMessage ? "error" : ""}`}
                    disabled={isSendingOtp}
                  />
                </div>
                {errorMessage && (
                  <Form.Text
                    id="phoneError"
                    className="text-danger error-message"
                    role="alert"
                  >
                    {errorMessage}
                    {isRateLimited && timeRemaining > 0 && (
                      <div style={{ marginTop: "5px", fontWeight: "bold" }}>
                        Time remaining: {formatTimeRemaining(timeRemaining)}
                      </div>
                    )}
                  </Form.Text>
                )}
              </Form.Group>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                size="invisible"
                badge="bottomright"
                style={{
                  visibility: 'hidden',
                  position: 'absolute',
                  left: '-9999px'
                }}
                onErrored={() => console.log("reCAPTCHA errored")}
                onExpired={() => console.log("reCAPTCHA expired")}
              />
              <button
                type="submit"
                disabled={isSendingOtp || isRateLimited}
                className="global-next-btn"
                aria-describedby={isSendingOtp ? "loadingStatus" : undefined}
                title={isRateLimited ? "Please wait before trying again" : ""}
              >
                {isSendingOtp ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : isRateLimited ? (
                  `Try again in ${formatTimeRemaining(timeRemaining)}`
                ) : (
                  "Next"
                )}
              </button>
            </Form>

            <Container className="or-option">
              <Container className="or-line">
                <div className="line" />
                <span>or</span>
                <div className="line" />
              </Container>
              <p className="text-link">
                Already have an account?{" "}
                <a href="/signinoptions" className="signup-signin">
                  Sign in here
                </a>
              </p>
              <p className="text-link">
                By continuing you accept our{" "}
                <a className="signup-links" href="/PrivacyPolicy">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a className="signup-links" target="_blank" rel="noopener noreferrer" href="/Tnc">
                  Terms of Use
                </a>
              </p>
            </Container>
          </Container>
        </Container>
      </Container>
    </div>
  );
};
